import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueue } from "../contexts/QueueContext";
import { useToast } from "../contexts/ToastContext";
import { useDocumentStore } from "../store/documentState";
import { useAuth } from "../contexts/AuthContext";
import {
  AbschlussberichtAnswer,
  AbschlussberichtDraft,
  AbschlussberichtStoredSubmission,
  AbschlussberichtSubmission,
  AbschlussberichtSummaryQuantities,
  AbschlussberichtTemplate,
} from "../types/abschlussbericht";
import {
  getAbschlussberichtTemplate,
  upsertAbschlussberichtDraft,
} from "../services/api/abschlussbericht";
import { isQueueItemInFlight, QueueItemLockTimeoutError, removeStaleQueueItem } from "../services/queueService";
import type { QueueItemsPermanentlyFailedDetail, QueueItemsSyncFinishedDetail } from "../interfaces/queue";
import { getEnv } from "../utils/env";

interface AbschlussberichtWizardProps {
  projectId: string;
  phaseId: string;
  disabled?: boolean;
  onCompleted?: (submittedAt: string) => void;
}

type AnswerMap = Record<string, AbschlussberichtAnswer>;
type ChecklistStateMap = Record<string, boolean[]>;
type DescriptionVisibilityMap = Record<string, boolean>;
type QuantityFieldKey = keyof AbschlussberichtSummaryQuantities;
type QuantityInputState = Record<QuantityFieldKey, string>;

interface AbschlussberichtLocalDraftState {
  answers: AnswerMap;
  checklistStates: ChecklistStateMap;
  descriptionVisibility: DescriptionVisibilityMap;
  currentStepIndex: number;
  confirmationAccepted: boolean;
  quantityInputs: QuantityInputState;
}

const EMPTY_QUANTITY_INPUTS: QuantityInputState = {
  ventileAnzahl: "",
  rohreInnen: "",
  rohreAussen: "",
};

type WarrantyPaymentChoice = NonNullable<AbschlussberichtAnswer["warrantyPaymentChoice"]>;

const WARRANTY_PAYMENT_OPTIONS: Array<{ value: WarrantyPaymentChoice; label: string }> = [
  { value: "monthly", label: "Kunde zahlt monatlich" },
  { value: "one_time", label: "Kunde zahlt einmalig" },
  { value: "sales_follow_up", label: "Vertriebler soll nachhaken" },
  { value: "no_extension", label: "Keine Garantieverlängerung gewünscht" },
];

const getWarrantyPaymentChoiceLabel = (
  choice?: AbschlussberichtAnswer["warrantyPaymentChoice"]
): string | null =>
  WARRANTY_PAYMENT_OPTIONS.find((option) => option.value === choice)?.label || null;

const getChecklistItemsFromHtml = (html: string): string[] => {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return [];
  }

  const document = new window.DOMParser().parseFromString(html, "text/html");
  return Array.from(document.querySelectorAll("li"))
    .map((item) => item.textContent?.replace(/\s+/g, " ").trim() || "")
    .filter(Boolean);
};

const toAnswerMap = (answers: AbschlussberichtAnswer[]): AnswerMap =>
  answers.reduce<AnswerMap>((accumulator, answer) => {
    accumulator[answer.stepId] = answer;
    return accumulator;
  }, {});

const normalizeQuantityInputs = (
  quantities?: Partial<AbschlussberichtSummaryQuantities> | null
): QuantityInputState => ({
  ventileAnzahl:
    typeof quantities?.ventileAnzahl === "number" && quantities.ventileAnzahl >= 0
      ? String(quantities.ventileAnzahl)
      : "",
  rohreInnen:
    typeof quantities?.rohreInnen === "number" && quantities.rohreInnen >= 0
      ? String(quantities.rohreInnen)
      : "",
  rohreAussen:
    typeof quantities?.rohreAussen === "number" && quantities.rohreAussen >= 0
      ? String(quantities.rohreAussen)
      : "",
});

const getQuantityValue = (value: string): number =>
  /^\d+$/.test(value) ? Number(value) : 0;

const getSummaryQuantitiesFromInputs = (
  quantityInputs: QuantityInputState
): AbschlussberichtSummaryQuantities => ({
  ventileAnzahl: getQuantityValue(quantityInputs.ventileAnzahl),
  rohreInnen: getQuantityValue(quantityInputs.rohreInnen),
  rohreAussen: getQuantityValue(quantityInputs.rohreAussen),
});

const normalizeChecklistStates = (
  checklistStates: ChecklistStateMap
): ChecklistStateMap =>
  Object.fromEntries(
    Object.entries(checklistStates).map(([stepId, values]) => [
      stepId,
      (values || []).map((value) => value === true),
    ])
  );

const buildSourceSignature = (template: AbschlussberichtTemplate): string =>
  JSON.stringify({
    projectId: template.projectId,
    sourceDocumentId: template.sourceDocumentId,
    sourceDocumentNumber: template.sourceDocumentNumber,
    templateKey: template.templateKey,
    templateVersion: template.templateVersion,
    steps: template.steps.map((step) => ({
      id: step.id,
      itemNumber: step.itemNumber,
      quantityLabel: step.quantityLabel,
      displayTitle: step.displayTitle,
      checklistHtml: step.checklistHtml,
      descriptionHtml: step.descriptionHtml,
    })),
  });

const clampStepIndex = (value: number, max: number) => Math.max(0, Math.min(value, max));

const SHOW_POSITION_CHECKLIST =
  getEnv("VITE_ENABLE_ABSCHLUSSBERICHT_POSITION_CHECKLIST") === "true";

// Standard: an. Nur der explizite Wert "false" blendet den Hinweis aus (#1625).
// Bewusst als Funktion (nicht Modul-Konstante): Die Auswertung pro Render erlaubt
// vi.stubEnv in den Tests, SHOW_POSITION_CHECKLIST ist dort nicht umschaltbar.
const isFoerderhoeheHinweisEnabled = () =>
  getEnv("VITE_ENABLE_ABSCHLUSSBERICHT_FOERDERHOEHE_HINWEIS") !== "false";

const PROJECT_COMPLETION_STEP: AbschlussberichtTemplate["steps"][number] = {
  id: "project-completion",
  itemNumber: "Abschluss",
  groupTitle: "Projekt Abschluss",
  displayTitle: "Projekt Abschluss",
  descriptionHtml:
    "<p>Das Projekt ist vollständig abgeschlossen.</p><ul><li>Ich habe die Baustelle vollständig abgeschlossen.</li><li>Es fallen keine Nacharbeiten an.</li><li>Das Projekt kann abgerechnet werden.</li></ul>",
  checklistHtml: "",
};

const isProjectCompletionStep = (stepId: string) => stepId === PROJECT_COMPLETION_STEP.id;

const normalizeStepSearchText = (value: string): string =>
  value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

type AbschlussberichtStep = AbschlussberichtTemplate["steps"][number];

const getWarrantyStepSearchText = (step: AbschlussberichtStep) => {
  const groupText = normalizeStepSearchText(step.groupTitle || "");
  const stepText = normalizeStepSearchText(
    [
      step.displayTitle,
      step.descriptionHtml,
      step.checklistHtml,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return { groupText, stepText };
};

const isWarrantyGroupStep = (step: AbschlussberichtStep): boolean => {
  const { groupText } = getWarrantyStepSearchText(step);
  return groupText.includes("garantie") && (groupText.includes("wartung") || groupText.includes("service"));
};

const isYearlyWarrantyPaymentStep = (step: AbschlussberichtStep): boolean => {
  const { groupText, stepText } = getWarrantyStepSearchText(step);
  const hasWarrantyContext =
    groupText.includes("garantie") ||
    groupText.includes("wartung") ||
    groupText.includes("service") ||
    stepText.includes("garantie") ||
    stepText.includes("wartung") ||
    stepText.includes("service");

  return hasWarrantyContext && stepText.includes("jahr");
};

const getWarrantyPaymentStepId = (steps: AbschlussberichtStep[]): string | null =>
  (steps.find(isYearlyWarrantyPaymentStep) || steps.find(isWarrantyGroupStep))?.id || null;

const EXCLUDED_GROUP_TITLE_KEYWORDS = ["forderservice", "detailplanung"];

const isExcludedCategoryStep = (step: AbschlussberichtStep): boolean => {
  const { groupText } = getWarrantyStepSearchText(step);
  return EXCLUDED_GROUP_TITLE_KEYWORDS.some((keyword) => groupText.includes(keyword));
};

const AbschlussberichtWizard: React.FC<AbschlussberichtWizardProps> = ({
  projectId,
  phaseId,
  disabled = false,
  onCompleted,
}) => {
  const { addToQueue, forgetQueueItem, items } = useQueue();
  const { showToast } = useToast();
  const { user } = useAuth();
  const getPhaseStatus = useDocumentStore((state) => state.getPhaseStatus);
  const updatePhaseStatus = useDocumentStore((state) => state.updatePhaseStatus);

  const [template, setTemplate] = useState<AbschlussberichtTemplate | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [localSubmission, setLocalSubmission] = useState<AbschlussberichtSubmission | null>(null);
  const [isRestartedForEditing, setIsRestartedForEditing] = useState(false);
  const [confirmationAccepted, setConfirmationAccepted] = useState(false);
  const [checklistStates, setChecklistStates] = useState<ChecklistStateMap>({});
  const [descriptionVisibility, setDescriptionVisibility] = useState<DescriptionVisibilityMap>({});
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [quantityInputs, setQuantityInputs] = useState<QuantityInputState>(EMPTY_QUANTITY_INPUTS);
  const autosaveRef = useRef<number | null>(null);
  // Vorgemerkter Draft-Payload, wenn der Autosave wegen eines noch laufenden In-Flight-Items
  // übersprungen wurde (siehe persistAbschlussberichtDraft/PR-Review-Finding zu #976) - der
  // Retry-Listener weiter unten stößt das Nachholen an, sobald das in-flight-Item fertig ist.
  const pendingDraftRetryRef = useRef<AbschlussberichtDraft | null>(null);

  // PR-Review-Finding (P1, 10. Re-Review zu #976): der revisionsgebundene Marker-Schutz (siehe
  // clearPendingRetryMarkerIfCurrent) verhindert zwar, dass ein älterer, abschließender
  // Direkt-PUT den localStorage-Marker eines noch laufenden neueren Versuchs mitlöscht - er
  // schützt aber NICHT die Reihenfolge der tatsächlichen Server-Schreibvorgänge. Antwortet der
  // Server auf den ÄLTEREN PUT NACH dem neueren (z.B. Netzwerk-Jitter), überschreibt der ältere
  // Payload den gerade erst gespeicherten neueren Stand serverseitig, ganz ohne dass danach noch
  // ein Marker auf die Diskrepanz hinweist. Diese Kette serialisiert alle Direkt-Versuche für
  // diese Wizard-Instanz strikt in Aufrufreihenfolge (analog zu withQueueItemLock/
  // withHubspotPropertyLock in queueService.ts, hier aber pro Komponente statt pro Queue-Item-
  // ID, da es um Direkt-PUTs außerhalb der Queue geht) - ein späterer Aufruf sendet seinen PUT
  // garantiert erst, nachdem der vorherige Aufruf (inkl. dessen Server-Antwort) vollständig
  // abgeschlossen ist, wodurch die Server-Schreibreihenfolge immer der Aufrufreihenfolge folgt.
  const persistAttemptChainRef = useRef<Promise<void>>(Promise.resolve());

  const visibleSteps = useMemo(
    () => (template ? template.steps.filter((step) => !isExcludedCategoryStep(step)) : []),
    [template]
  );
  const steps = useMemo(
    () => (template ? [...visibleSteps, PROJECT_COMPLETION_STEP] : []),
    [template, visibleSteps]
  );
  const warrantyPaymentStepId = useMemo(
    () => (template ? getWarrantyPaymentStepId(visibleSteps) : null),
    [template, visibleSteps]
  );
  const quantitiesStepIndex = steps.length;
  const summaryStepIndex = steps.length + 1;
  const queueSubmission = useMemo(() => {
    const queueItem = [...items]
      .reverse()
      .find(
        (item) =>
          item.type === "abschlussbericht_submit" &&
          item.data?.projectId === projectId &&
          item.data?.phaseId === phaseId
      );

    return (queueItem?.data?.submission || null) as AbschlussberichtSubmission | null;
  }, [items, phaseId, projectId]);

  const readOnlySubmission = useMemo<AbschlussberichtStoredSubmission | AbschlussberichtSubmission | null>(() => {
    if (isRestartedForEditing) {
      return null;
    }
    // queueSubmission/localSubmission zuerst: sie spiegeln die zuletzt vom Nutzer bestätigte
    // Eingabe wider, die noch nicht garantiert beim Server angekommen ist. template.existing-
    // Submission wurde beim Laden des Templates einmalig geladen und kann durch eine noch
    // laufende oder in der Queue wartende Korrektur bereits veraltet sein — würde sie hier
    // Vorrang haben, würde eine gerade erst korrigierte Position wieder als "Nein" angezeigt
    // (#976), obwohl der korrigierte Wert lokal bzw. in der Queue bereits vorliegt.
    return queueSubmission || localSubmission || template?.existingSubmission || null;
  }, [isRestartedForEditing, localSubmission, queueSubmission, template?.existingSubmission]);

  const draftStorageKey = useMemo(
    () =>
      template
        ? `abschlussbericht-wizard-draft:${projectId}:${phaseId}:${template.templateKey}:${template.templateVersion}:${template.sourceDocumentId}`
        : null,
    [phaseId, projectId, template]
  );

  const draftQueueKey = useMemo(
    () =>
      template
        ? `abschlussbericht-draft:${projectId}:${template.templateVersion}:${template.sourceDocumentId}`
        : null,
    [projectId, template]
  );

  // PR-Review-Finding (P1, 7. Re-Review zu #976): pendingDraftRetryRef ist reiner In-Memory-
  // State - navigiert der Nutzer weg oder lädt neu, BEVOR das nächste queue-item-sync-finished-
  // Event eintrifft, geht der vorgemerkte Payload verloren. Zusätzlich kann QueueProvider das
  // alte Queue-Item schon verarbeiten und das Event senden, bevor dieser Wizard (nach Reload)
  // überhaupt neu gemountet und sein Listener registriert ist. Dieser localStorage-Key spiegelt
  // pendingDraftRetryRef dauerhaft, damit ein neu gemounteter Wizard den vorgemerkten Payload
  // beim Start selbst nachholen kann, unabhängig davon, ob ein passendes Event je eintrifft.
  const draftPendingRetryStorageKey = useMemo(
    () => (draftQueueKey ? `abschlussbericht-wizard-pending-retry:${draftQueueKey}` : null),
    [draftQueueKey]
  );

  // Als eigener Wert (statt inline in handleSubmit) gehalten, damit der Permanent-Fehler-
  // Listener weiter unten (Finding zu #976) denselben Key zum Abgleich verwenden kann.
  const submitQueueKey = useMemo(
    () => `abschlussbericht_submit:${projectId}:${phaseId}`,
    [projectId, phaseId]
  );

  const isQuantitiesStep = currentStepIndex === quantitiesStepIndex;
  const isSummaryStep = currentStepIndex === summaryStepIndex;
  const currentStep = !isQuantitiesStep && !isSummaryStep ? steps[currentStepIndex] : null;
  const currentAnswer = currentStep ? answers[currentStep.id] : null;
  const currentStepRequiresWarrantyPaymentChoice = currentStep
    ? currentStep.id === warrantyPaymentStepId
    : false;
  const currentChecklistHtml = currentStep?.checklistHtml?.trim()
    ? currentStep.checklistHtml
    : currentStep?.descriptionHtml || "";
  const currentChecklistItems = useMemo(
    () => (currentChecklistHtml ? getChecklistItemsFromHtml(currentChecklistHtml) : []),
    [currentChecklistHtml]
  );
  const currentChecklistState = currentStep ? checklistStates[currentStep.id] || [] : [];
  const isCurrentDescriptionExpanded = currentStep
    ? isProjectCompletionStep(currentStep.id) || !!descriptionVisibility[currentStep.id]
    : false;
  const areAllCurrentChecklistItemsChecked =
    currentChecklistItems.length > 0 &&
    currentChecklistItems.every((_, index) => !!currentChecklistState[index]);
  const summaryQuantities = useMemo(
    () => getSummaryQuantitiesFromInputs(quantityInputs),
    [quantityInputs]
  );
  const hasValidSummaryQuantities =
    quantityInputs.ventileAnzahl !== "" &&
    quantityInputs.rohreInnen !== "" &&
    quantityInputs.rohreAussen !== "" &&
    summaryQuantities.ventileAnzahl >= 0 &&
    summaryQuantities.rohreInnen >= 0 &&
    summaryQuantities.rohreAussen >= 0;

  useEffect(() => {
    let isMounted = true;

    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAbschlussberichtTemplate(projectId);
        if (!isMounted) {
          return;
        }

        setTemplate(response);
        setAnswers({});
        setChecklistStates({});
        setDescriptionVisibility({});
        setCurrentStepIndex(0);
        setConfirmationAccepted(false);
        setDraftHydrated(false);
        setQuantityInputs(EMPTY_QUANTITY_INPUTS);

        if (queueSubmission) {
          // Eine neuere, noch nicht synchronisierte Korrektur liegt in der Queue vor (siehe
          // readOnlySubmission-Priorisierung, #976) - ihre Metadaten müssen auch hier Vorrang
          // vor der potenziell veralteten Server-Submission haben. Sonst zeigt die Read-only-
          // Ansicht bereits die korrigierte Antwort, während getPhaseStatus() (z.B. in
          // Fortschritts-/Phasenlisten-Screens) noch Zeitstempel/Autor der alten Submission
          // trägt - eine sichtbare Inkonsistenz zwischen zwei UI-Oberflächen (PR-Review-
          // Finding zu #416).
          updatePhaseStatus(
            phaseId,
            "completed",
            queueSubmission.submittedAt,
            queueSubmission.submittedByEmail
          );
          onCompleted?.(queueSubmission.submittedAt);
        } else if (response.existingSubmission) {
          updatePhaseStatus(
            phaseId,
            "completed",
            response.existingSubmission.submittedAt,
            response.existingSubmission.submittedByEmail
          );
          onCompleted?.(response.existingSubmission.submittedAt);
        } else {
          updatePhaseStatus(phaseId, "pending");
        }
      } catch (loadError) {
        const message =
          loadError instanceof Error
            ? loadError.message
            : "Abschlussbericht konnte nicht geladen werden";
        if (isMounted) {
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTemplate();
    return () => {
      isMounted = false;
    };
  }, [phaseId, projectId]);

  useEffect(() => {
    if (!queueSubmission) {
      return;
    }

    setLocalSubmission(queueSubmission);

    // Eigener Effect statt Teil des loadTemplate-Effects oben (PR-Review-Finding zu #976):
    // `queueSubmission` wird von QueueContext asynchron aus IndexedDB nachgeladen und kann
    // erst NACH dem Abschluss von loadTemplate verfügbar werden. Der dortige Branch hängt nur
    // von phaseId/projectId ab und liest queueSubmission daher nur aus dem Render, der ihn
    // gestartet hat — schlägt der initiale Queue-Load transient fehl oder ist schlicht noch
    // nicht fertig, würde diese Priorisierung nie nachgeholt und Phasenstatus/onCompleted
    // blieben dauerhaft auf der veralteten Server-Submission stehen (sichtbare Inkonsistenz
    // zur readOnlySubmission-Ansicht, die queueSubmission bereits korrekt bevorzugt). Dieser
    // Effect hängt direkt von queueSubmission ab und holt die Priorisierung in dem Fall nach,
    // sobald der Wert eintrifft — unabhängig davon, wann das gegenüber loadTemplate passiert.
    updatePhaseStatus(
      phaseId,
      "completed",
      queueSubmission.submittedAt,
      queueSubmission.submittedByEmail
    );
    onCompleted?.(queueSubmission.submittedAt);
  }, [onCompleted, phaseId, queueSubmission, updatePhaseStatus]);

  // PR-Review-Finding (P1) zu #976: Ein per addToQueue OFFLINE eingereihter Submit löst dessen
  // Promise sofort auf (siehe QueueContext.addToQueue) - der `.then`-Handler in handleSubmit
  // unten sieht daher NIE das Ergebnis des eigentlichen Sync-Versuchs, der erst viel später
  // (nächster Online-Reconnect, siehe QueueContext.handleOnlineStatus) und in einem komplett
  // anderen Aufrufkontext läuft. Scheitert dieser spätere Versuch endgültig (4xx), entfernt
  // processQueue das Item zwar aus der Queue, aber ohne diesen Listener bliebe `localSubmission`
  // (und damit readOnlySubmission) dauerhaft auf der nie gespeicherten Korrektur stehen. Der
  // Abgleich über `submittedAt` verhindert, dass ein verspätet eintreffendes Event fälschlich
  // eine ZWISCHENZEITLICH bereits erneut abgeschickte und erfolgreiche Korrektur zurückrollt.
  //
  // PR-Review-Finding (P1, Re-Review): War die fehlgeschlagene Übermittlung eine KORREKTUR
  // eines bereits vorhandenen Server-Abschlusses (template.existingSubmission), darf der
  // Rollback die Phase NICHT auf "pending" setzen - der alte, weiterhin gültige Serverstand
  // muss wiederhergestellt werden. "pending" ist nur korrekt, wenn es noch nie eine
  // erfolgreiche Übermittlung gab (Erstübermittlung schlägt fehl).
  useEffect(() => {
    const handlePermanentFailure = (event: Event) => {
      const detail = (event as CustomEvent<QueueItemsPermanentlyFailedDetail>).detail;
      const failedItem = detail?.permanentlyFailedItems.find((item) => item.id === submitQueueKey);
      const failedSubmission = (failedItem?.data as { submission?: AbschlussberichtSubmission } | undefined)
        ?.submission;

      if (!failedSubmission || !localSubmission || failedSubmission.submittedAt !== localSubmission.submittedAt) {
        return;
      }

      setLocalSubmission(null);
      if (template?.existingSubmission) {
        updatePhaseStatus(
          phaseId,
          "completed",
          template.existingSubmission.submittedAt,
          template.existingSubmission.submittedByEmail
        );
      } else {
        updatePhaseStatus(phaseId, "pending");
      }
      showToast("error", "Abschlussbericht konnte nicht gespeichert werden", 4000);
    };

    window.addEventListener("queue-item-permanently-failed", handlePermanentFailure);
    return () => window.removeEventListener("queue-item-permanently-failed", handlePermanentFailure);
  }, [localSubmission, phaseId, showToast, submitQueueKey, template, updatePhaseStatus]);

  useEffect(() => {
    if (!template) {
      return;
    }

    if (template.existingSubmission) {
      setAnswers(toAnswerMap(template.existingSubmission.answers));
      setQuantityInputs(normalizeQuantityInputs(template.existingSubmission.summaryQuantities));
      setChecklistStates({});
      setDescriptionVisibility({});
      setCurrentStepIndex(summaryStepIndex);
      setConfirmationAccepted(true);
      setDraftHydrated(true);
      return;
    }

    if (template.existingDraft) {
      setAnswers(toAnswerMap(template.existingDraft.answers));
      setQuantityInputs(normalizeQuantityInputs(template.existingDraft.summaryQuantities));
      setChecklistStates(template.existingDraft.checklistStates || {});
      setDescriptionVisibility({});
      setCurrentStepIndex(clampStepIndex(template.existingDraft.currentStepIndex, summaryStepIndex));
      setConfirmationAccepted(false);
      setDraftHydrated(true);
      return;
    }

    if (!draftStorageKey || queueSubmission || localSubmission) {
      setDraftHydrated(true);
      return;
    }

    try {
      const rawDraft = window.localStorage.getItem(draftStorageKey);
      if (!rawDraft) {
        setDraftHydrated(true);
        return;
      }

      const draft = JSON.parse(rawDraft) as Partial<AbschlussberichtLocalDraftState>;
      setAnswers(draft.answers || {});
      setChecklistStates(draft.checklistStates || {});
      setDescriptionVisibility(draft.descriptionVisibility || {});
      setCurrentStepIndex(
        clampStepIndex(
          typeof draft.currentStepIndex === "number" ? draft.currentStepIndex : 0,
          summaryStepIndex
        )
      );
      setConfirmationAccepted(!!draft.confirmationAccepted);
      setQuantityInputs(draft.quantityInputs || EMPTY_QUANTITY_INPUTS);
    } catch {
      window.localStorage.removeItem(draftStorageKey);
    } finally {
      setDraftHydrated(true);
    }
  }, [draftStorageKey, localSubmission, queueSubmission, summaryStepIndex, template]);

  useEffect(() => {
    if (!draftStorageKey || !draftHydrated) {
      return;
    }

    if (readOnlySubmission) {
      window.localStorage.removeItem(draftStorageKey);
      return;
    }

    const draft: AbschlussberichtLocalDraftState = {
      answers,
      checklistStates,
      descriptionVisibility,
      currentStepIndex,
      confirmationAccepted,
      quantityInputs,
    };

    window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
  }, [
    answers,
    checklistStates,
    confirmationAccepted,
    currentStepIndex,
    descriptionVisibility,
    draftHydrated,
    draftStorageKey,
    quantityInputs,
    readOnlySubmission,
  ]);

  useEffect(() => {
    if (!currentStep || !areAllCurrentChecklistItemsChecked) {
      return;
    }

    setAnswers((previous) => {
      const existing = previous[currentStep.id];
      if (existing?.planned) {
        return previous;
      }

      return {
        ...previous,
        [currentStep.id]: {
          stepId: currentStep.id,
          itemNumber: currentStep.itemNumber,
          displayTitle: currentStep.displayTitle,
          planned: true,
        },
      };
    });
  }, [areAllCurrentChecklistItemsChecked, currentStep]);

  // Spiegelt pendingDraftRetryRef dauerhaft in localStorage (siehe draftPendingRetryStorageKey-
  // Doku oben) - schreibt IMMER den übergebenen Payload. Fehler beim Schreiben werden nur
  // geloggt: der In-Memory-Ref bleibt so oder so die primäre, sofort wirksame Quelle innerhalb
  // derselben Session, localStorage ist nur die zusätzliche Absicherung über einen
  // Reload/Remount hinweg.
  const persistPendingRetryMarker = useCallback(
    (payload: AbschlussberichtDraft) => {
      if (!draftPendingRetryStorageKey) {
        return;
      }
      try {
        window.localStorage.setItem(draftPendingRetryStorageKey, JSON.stringify(payload));
      } catch (storageError) {
        console.error(
          '[Abschlussbericht] Pending-Retry-Marker konnte nicht in localStorage geschrieben werden:',
          storageError
        );
      }
    },
    [draftPendingRetryStorageKey]
  );

  // Entfernt den Marker NUR, wenn er noch exakt zu `draftPayload` gehört (gleiches `updatedAt`)
  // - PR-Review-Finding (P1, 9. Re-Review zu #976): persistAbschlussberichtDraft serialisiert
  // überlappende Direkt-PUTs für denselben draftQueueKey nicht (isQueueItemInFlight schützt nur
  // queue-basierte Sync-Versuche, nicht diese Direktversuche). Startet während eines langsamen
  // PUTs nach einem weiteren Edit ein zweiter Autosave, schreibt dieser SEINEN (neueren) Payload
  // in denselben Marker-Schlüssel. Würde der ältere, zuerst gestartete Aufruf beim Abschluss den
  // Marker unbedingt löschen, risse er den noch laufenden neueren Versuch mit - ein Reload in
  // diesem Fenster verlöre den zuletzt eingegebenen Stand erneut. Ein inzwischen von einem
  // neueren Aufruf überschriebener Marker bleibt unangetastet und wird von dessen eigenem
  // Abschluss verwaltet.
  const clearPendingRetryMarkerIfCurrent = useCallback(
    (draftPayload: AbschlussberichtDraft) => {
      if (!draftPendingRetryStorageKey) {
        return;
      }
      try {
        const raw = window.localStorage.getItem(draftPendingRetryStorageKey);
        if (!raw) {
          return;
        }
        const stored = JSON.parse(raw) as AbschlussberichtDraft;
        if (stored.updatedAt === draftPayload.updatedAt) {
          window.localStorage.removeItem(draftPendingRetryStorageKey);
        }
      } catch (storageError) {
        console.error(
          '[Abschlussbericht] Pending-Retry-Marker konnte nicht revisionsgebunden entfernt werden:',
          storageError
        );
      }
    },
    [draftPendingRetryStorageKey]
  );

  // Persistiert einen Draft-Payload (online direkt, offline/bei Fehler über die Queue) - als
  // eigene Funktion extrahiert (statt inline im Autosave-Timeout unten), damit sowohl der
  // debounced Autosave als auch der Retry-Listener weiter unten dieselbe Logik nutzen können.
  const persistAbschlussberichtDraft = useCallback(
    async (draftPayload: AbschlussberichtDraft) => {
      if (!draftQueueKey) {
        return;
      }

      if (isQueueItemInFlight(draftQueueKey)) {
        // Ein vorheriger Draft-Sync für dieses Projekt läuft noch (Request raus, Antwort
        // steht aus). addToQueue würde den noch nicht gesendeten, neueren Entwurf
        // überschreiben — läuft der alte Request danach erfolgreich durch, löscht
        // processQueue das Item anhand der gemeinsamen queueKey-ID und der neuere Entwurf
        // geht spurlos verloren (dasselbe Muster wie beim Submit, siehe #976). PR-Review-
        // Finding: statt den Entwurf hier einfach fallen zu lassen (er würde nur beim
        // nächsten NUTZER-Edit erneut versucht), merken wir ihn vor - der Retry-Listener
        // weiter unten holt ihn nach, sobald das in-flight-Item fertig ist (Erfolg ODER
        // endgültiger Fehlschlag), auch ohne eine weitere Nutzeränderung.
        pendingDraftRetryRef.current = draftPayload;
        persistPendingRetryMarker(draftPayload);
        return;
      }

      pendingDraftRetryRef.current = null;
      // PR-Review-Finding (P1, 8. Re-Review zu #976): den Marker hier VOR dem eigentlichen
      // Versuch zu löschen, öffnet dasselbe verlorene-Retry-Fenster erneut - wird die Seite
      // WÄHREND des PUTs oder der anschließenden Bereinigung neu geladen bzw. der Wizard
      // unmountet, sind dann sowohl der In-Memory-Ref als auch der durable Marker weg, obwohl
      // der sichere Endzustand noch gar nicht feststeht. Der Marker wird daher für DIESEN
      // Payload geschrieben (nicht gelöscht) und bleibt bestehen, bis unten tatsächlich ein
      // sicherer Endzustand bestätigt ist (PUT + Bereinigung erfolgreich, ODER der Payload liegt
      // durabel in der Queue) - erst dann wird er gelöscht.
      persistPendingRetryMarker(draftPayload);

      // PR-Review-Finding (P1, 10. Re-Review zu #976): der revisionsgebundene Marker-Schutz
      // (clearPendingRetryMarkerIfCurrent) allein reicht nicht - er schützt nur den lokalen
      // Marker, nicht die Reihenfolge der tatsächlichen Server-Schreibvorgänge. Antwortet der
      // Server auf einen ÄLTEREN, noch überlappend laufenden PUT NACH dem neueren (z.B.
      // Netzwerk-Jitter), überschreibt der ältere Payload den gerade erst gespeicherten
      // neueren Stand serverseitig - ganz ohne dass danach noch ein Marker auf die Diskrepanz
      // hinweist. runAttempt wird daher über persistAttemptChainRef strikt in Aufrufreihenfolge
      // verkettet (siehe Doku dort): der PUT dieses Aufrufs wird garantiert erst gesendet,
      // nachdem ein vorheriger, noch laufender Aufruf für dieselbe Wizard-Instanz vollständig
      // abgeschlossen ist (inkl. dessen Server-Antwort) - die Server-Schreibreihenfolge folgt
      // damit immer der Aufrufreihenfolge, ein neuerer Payload kann nie mehr von einem älteren
      // überholt werden.
      const runAttempt = async () => {
        // PR-Review-Finding (P1, 11. Re-Review zu #976): der In-Flight-Guard ganz oben wurde
        // beim AUFRUF von persistAbschlussberichtDraft geprüft, nicht beim tatsächlichen Start
        // dieses Versuchs. Wartet dieser Aufruf hier in persistAttemptChainRef hinter einem
        // vorherigen, noch laufenden Aufruf, kann sich der In-Flight-Status in der Zwischenzeit
        // geändert haben: scheitert der vorherige Direkt-PUT und wird über addToQueue in die
        // Queue geschrieben, kann dessen Sync SOFORT anlaufen und denselben draftQueueKey über
        // isQueueItemInFlight als in-flight markieren - unabhängig von persistAttemptChainRef
        // (zwei getrennte Mechanismen). Ein jetzt trotzdem gestarteter Direkt-PUT könnte vom
        // parallel laufenden Queue-Sync überholt werden oder dessen Queue-Eintrag per
        // gemeinsamem Key entfernen. Daher hier - unmittelbar vor dem tatsächlichen Start -
        // erneut prüfen, statt sich auf den Snapshot vom Aufrufzeitpunkt zu verlassen.
        if (isQueueItemInFlight(draftQueueKey)) {
          pendingDraftRetryRef.current = draftPayload;
          return;
        }
        try {
          if (navigator.onLine) {
            await upsertAbschlussberichtDraft(projectId, draftPayload);
            // PR-Review-Finding (P1, Re-Review): ein per Direkt-PUT gespeicherter Stand ist
            // aktueller als alles, was noch unter demselben Queue-Key in der Offline-Queue liegt
            // - z.B. ein zuvor NUR TRANSIENT gescheiterter Sync-Versuch (Netzwerk/Timeout/5xx),
            // der mit erhöhtem Retry-Zähler weiter in der Queue steht, während `navigator.onLine`
            // bei so einem Fehler weiterhin `true` bleibt. Ohne Bereinigung würde der spätere
            // automatische Retry dieses veralteten Items den gerade gespeicherten neueren Stand
            // wieder überschreiben.
            //
            // PR-Review-Finding (P1, 3. Re-Review): ein separates loadQueue()+removeFromQueue()
            // ist trotz `updatedAt`-Vergleich kein atomarer Vorgang - zwischen beiden Aufrufen
            // kann ein weiterer Autosave mit NEUEREM Payload über addToQueue unter demselben Key
            // vormerken und würde von der Löschung mitgerissen. `removeStaleQueueItem` führt
            // Lesen, Vergleichen und Löschen innerhalb desselben ID-Locks aus (siehe
            // `withQueueItemLock` in queueService.ts, geteilt mit `addQueueItem`) und ist damit
            // gegen genau dieses Fenster abgesichert.
            try {
              const wasRemoved = await removeStaleQueueItem(
                draftQueueKey,
                (currentItem) => {
                  const queuedDraft = (currentItem.data as { draft?: AbschlussberichtDraft } | undefined)?.draft;
                  return !!queuedDraft && queuedDraft.updatedAt <= draftPayload.updatedAt;
                },
                {
                  id: draftQueueKey,
                  type: "abschlussbericht_draft_upsert",
                  retries: 0,
                  createdAt: new Date(),
                  data: { queueKey: draftQueueKey, projectId, draft: draftPayload },
                }
              );

              if (wasRemoved) {
                // IndexedDB ist bereits konsistent (removeStaleQueueItem hat dort schon,
                // innerhalb des ID-Locks, entfernt) - hier nur noch den React-Queue-State
                // (Badge/Queue-Liste) synchronisieren. Bewusst forgetQueueItem statt erneut
                // removeFromQueue (PR-Review-Finding, 4. Re-Review): removeFromQueue würde
                // removeQueueItem(id) ein zweites Mal aufrufen - diesmal UNGESCHÜTZT, außerhalb
                // des Locks - und könnte damit einen zwischenzeitlich (nach Freigabe des Locks)
                // neu eingereihten, nie gesendeten Payload für dieselbe ID mitlöschen.
                forgetQueueItem(draftQueueKey);
              }
              // Sicherer Endzustand erreicht: Direkt-PUT erfolgreich, Queue bereinigt (oder war
              // ohnehin nicht veraltet) - der Marker kann jetzt entfernt werden (revisionsgebunden,
              // siehe clearPendingRetryMarkerIfCurrent-Doku).
              clearPendingRetryMarkerIfCurrent(draftPayload);
            } catch (cleanupError) {
              // PR-Review-Finding (P1, 5. Re-Review zu #976): reines Loggen stellt den sicheren
              // Zustand NICHT her - ein Wurf aus removeStaleQueueItem bedeutet "Bereinigungsstatus
              // unbekannt" (Lesefehler) oder "definitiv nicht bereinigt" (Doppel-Fehlschlag aus
              // Entfernen UND Ersatzschreiben), in beiden Fällen könnte ein tatsächlich noch
              // vorhandenes, veraltetes Queue-Item später den gerade per Direkt-PUT gespeicherten
              // neueren Stand überschreiben. Statt uns auf die (möglicherweise gescheiterte)
              // Bereinigung zu verlassen, merken wir den aktuellen (bereits erfolgreich auf dem
              // Server gespeicherten) Payload sicherheitshalber ERNEUT über addToQueue vor -
              // dasselbe überschreibt ein evtl. noch vorhandenes veraltetes Item unter demselben
              // Queue-Key direkt mit dem korrekten Stand (addQueueItem läuft über denselben
              // ID-Lock wie removeStaleQueueItem, siehe queueService.ts).
              console.error(
                '[Abschlussbericht] Draft-Queue-Bereinigung nach Direkt-PUT fehlgeschlagen - merke aktuellen Stand sicherheitshalber erneut für die Queue vor:',
                cleanupError
              );
              try {
                await addToQueue("abschlussbericht_draft_upsert", {
                  queueKey: draftQueueKey,
                  projectId,
                  draft: draftPayload,
                });
                // Sicherer Endzustand: der korrekte Stand liegt jetzt durabel in der Queue -
                // selbst falls das veraltete Item nie bereinigt wird, gewinnt bei einem
                // gemeinsamen queueKey ohnehin dieser gerade geschriebene, korrekte Eintrag.
                clearPendingRetryMarkerIfCurrent(draftPayload);
              } catch (requeueError) {
                // PR-Review-Finding (P1, 6. Re-Review zu #976): scheitert auch dieses Re-Vormerken
                // (z.B. dasselbe zugrunde liegende IndexedDB-/Quota-Problem), reichte reines
                // Loggen bisher nicht - pendingDraftRetryRef wurde oben bereits geleert, der
                // Payload wäre also ohne weitere Nutzeränderung nie wieder versucht worden. Jetzt
                // erneut vorgemerkt: der bestehende queue-item-sync-finished-Listener (siehe
                // unten) holt ihn zuverlässig nach, sobald ein späterer Sync-Versuch für
                // draftQueueKey abschließt (z.B. beim nächsten Online-Reconnect) - unabhängig
                // davon, ob dieser Versuch selbst erfolgreich ist.
                console.error(
                  '[Abschlussbericht] Aktueller Stand konnte nach fehlgeschlagener Draft-Queue-Bereinigung nicht erneut vorgemerkt werden - merke ihn für einen späteren automatischen Retry vor:',
                  requeueError
                );
                pendingDraftRetryRef.current = draftPayload;
                persistPendingRetryMarker(draftPayload);
              }
            }
          } else {
            await addToQueue("abschlussbericht_draft_upsert", {
              queueKey: draftQueueKey,
              projectId,
              draft: draftPayload,
            });
            // Sicherer Endzustand: der Payload liegt durabel in der Queue.
            clearPendingRetryMarkerIfCurrent(draftPayload);
          }
        } catch {
          try {
            await addToQueue("abschlussbericht_draft_upsert", {
              queueKey: draftQueueKey,
              projectId,
              draft: draftPayload,
            });
            // Sicherer Endzustand: der Payload liegt durabel in der Queue.
            clearPendingRetryMarkerIfCurrent(draftPayload);
          } catch (queueError) {
            // addToQueue kann jetzt bei einem echten Persistenzfehler werfen (z.B.
            // Speicherplatz voll, siehe #1199) - ohne diesen catch wäre der Autosave-Zyklus
            // eine unhandled promise rejection und der Entwurf spurlos verloren.
            //
            // PR-Review-Finding (P1, 6./8. Re-Review zu #976): dieser Fallback-Requeue kann
            // ebenso scheitern wie der weiter oben behandelte - derselbe Grund gilt hier
            // symmetrisch: der Marker (bereits zu Beginn dieses Versuchs geschrieben und nie
            // gelöscht) bleibt bestehen, zusätzlich den In-Memory-Ref erneut setzen, damit ein
            // noch laufender queue-item-sync-finished-Listener ihn ohne Umweg über localStorage
            // sofort nachholen kann.
            console.error('[Abschlussbericht] Entwurf-Autosave konnte nicht in die Queue geschrieben werden:', queueError);
            pendingDraftRetryRef.current = draftPayload;
          }
        }
      };

      const attempt = persistAttemptChainRef.current.then(runAttempt, runAttempt);
      // Folgefehler dürfen die Kette nicht dauerhaft blockieren - ein Aufrufer, der scheitert,
      // hat den Fehler bereits selbst behandelt (siehe runAttempt), der nächste Aufruf in der
      // Kette soll trotzdem starten können.
      persistAttemptChainRef.current = attempt.catch(() => undefined);
      await attempt;
    },
    [
      addToQueue,
      clearPendingRetryMarkerIfCurrent,
      draftQueueKey,
      forgetQueueItem,
      persistPendingRetryMarker,
      projectId,
    ]
  );

  // PR-Review-Finding (P1) zu #976: Der In-Flight-Guard oben verwirft einen neueren Draft-
  // Autosave zwar nicht mehr vollständig (siehe pendingDraftRetryRef), aber ohne diesen
  // Listener würde der vorgemerkte Entwurf nur beim NÄCHSTEN Nutzer-Edit erneut versucht -
  // ändert der Nutzer während eines langsamen Syncs genau einmal etwas und dann nichts mehr,
  // gäbe es keinen weiteren Autosave-Zyklus mehr. `queue-item-sync-finished` feuert für JEDES
  // Ende eines In-Flight-Versuchs - Erfolg, endgültiger Fehlschlag ODER transienter Fehlschlag
  // mit Retry (PR-Review-Finding, Re-Review: die vorherige Beschränkung auf `project-status-
  // refresh`/`queue-item-permanently-failed` ließ den vorgemerkten Entwurf nach einem
  // transienten Fehlschlag - Netzwerk/Timeout/5xx - unbegrenzt liegen, da `processQueue` in
  // dem Fall keines der beiden Events sendet). Damit reicht ein einziger Listener.
  useEffect(() => {
    if (!draftQueueKey) {
      return;
    }

    const handleSyncFinished = (event: Event) => {
      const detail = (event as CustomEvent<QueueItemsSyncFinishedDetail>).detail;
      if (!detail?.finishedIds.includes(draftQueueKey) || !pendingDraftRetryRef.current) {
        return;
      }

      const payload = pendingDraftRetryRef.current;
      pendingDraftRetryRef.current = null;
      void persistAbschlussberichtDraft(payload);
    };

    window.addEventListener("queue-item-sync-finished", handleSyncFinished);
    return () => window.removeEventListener("queue-item-sync-finished", handleSyncFinished);
  }, [draftQueueKey, persistAbschlussberichtDraft]);

  // PR-Review-Finding (P1, 7. Re-Review zu #976): pendingDraftRetryRef ist reiner In-Memory-
  // State und geht bei Unmount (Navigation weg, Reload) verloren. Zusätzlich kann QueueProvider
  // ein altes Queue-Item schon verarbeiten und queue-item-sync-finished senden, BEVOR dieser
  // Wizard nach einem Reload überhaupt neu gemountet und der Listener oben registriert ist -
  // der Retry-Listener allein deckt einen Reload/Remount also nicht zuverlässig ab. Dieser
  // Effect holt einen in localStorage hinterlegten Pending-Retry-Marker (siehe
  // persistPendingRetryMarker) beim Start selbst nach, sobald draftQueueKey verfügbar ist -
  // unabhängig davon, ob inzwischen ein passendes Event eingetroffen ist.
  useEffect(() => {
    if (!draftPendingRetryStorageKey) {
      return;
    }

    let raw: string | null;
    try {
      raw = window.localStorage.getItem(draftPendingRetryStorageKey);
    } catch (readError) {
      console.error('[Abschlussbericht] Pending-Retry-Marker konnte nicht gelesen werden:', readError);
      return;
    }

    if (!raw) {
      return;
    }

    try {
      const payload = JSON.parse(raw) as AbschlussberichtDraft;
      void persistAbschlussberichtDraft(payload);
    } catch (parseError) {
      console.error(
        '[Abschlussbericht] Vorgemerkter Pending-Retry-Draft war ungültig und wird verworfen:',
        parseError
      );
      window.localStorage.removeItem(draftPendingRetryStorageKey);
    }
  }, [draftPendingRetryStorageKey, persistAbschlussberichtDraft]);

  useEffect(() => {
    if (!template || !draftHydrated || readOnlySubmission || !draftQueueKey) {
      return;
    }

    if (autosaveRef.current) {
      window.clearTimeout(autosaveRef.current);
    }

    autosaveRef.current = window.setTimeout(() => {
      const draftPayload: AbschlussberichtDraft = {
        projectId,
        sourceDocumentId: template.sourceDocumentId,
        sourceDocumentNumber: template.sourceDocumentNumber,
        templateKey: template.templateKey,
        templateVersion: template.templateVersion,
        sourceSignature: buildSourceSignature(template),
        checklistStates: SHOW_POSITION_CHECKLIST
          ? normalizeChecklistStates(checklistStates)
          : {},
        currentStepIndex,
        updatedAt: new Date().toISOString(),
        answers: steps.reduce<AbschlussberichtAnswer[]>((collected, step) => {
          const answer = answers[step.id];
          if (!answer) {
            return collected;
          }

          const isWarrantyStep = step.id === warrantyPaymentStepId;
          collected.push({
            stepId: step.id,
            itemNumber: step.itemNumber,
            displayTitle: step.displayTitle,
            planned: answer.planned,
            comment: answer.comment?.trim() || undefined,
            reworkRequired: answer.planned ? undefined : answer.reworkRequired,
            warrantyPaymentChoice: isWarrantyStep
              ? answer.warrantyPaymentChoice
              : undefined,
          });
          return collected;
        }, []),
        summaryQuantities,
      };

      void persistAbschlussberichtDraft(draftPayload);
    }, 750);

    return () => {
      if (autosaveRef.current) {
        window.clearTimeout(autosaveRef.current);
      }
    };
  }, [
    answers,
    checklistStates,
    currentStepIndex,
    draftHydrated,
    draftQueueKey,
    persistAbschlussberichtDraft,
    projectId,
    readOnlySubmission,
    steps,
    summaryQuantities,
    template,
    warrantyPaymentStepId,
  ]);

  const phaseStatus = getPhaseStatus(phaseId);
  const isCompleted = phaseStatus?.status === "completed";
  const isQueued = !!queueSubmission && !isCompleted;
  const isStepAnswerComplete = (
    step: AbschlussberichtTemplate["steps"][number],
    answer?: AbschlussberichtAnswer
  ): boolean => {
    if (!answer) {
      return false;
    }

    // Bei "Nein" muss zusätzlich explizit entschieden sein, ob eine Nacharbeit nötig ist.
    // Die API liefert für Alt-Entwürfe `null` statt fehlendem Feld - das gilt ebenfalls als unbeantwortet.
    const hasCompletionAnswer =
      answer.planned ||
      (!!answer.comment?.trim() && typeof answer.reworkRequired === "boolean");
    const hasWarrantyPaymentChoice =
      step.id !== warrantyPaymentStepId || !!answer.warrantyPaymentChoice;

    return hasCompletionAnswer && hasWarrantyPaymentChoice;
  };
  const isCurrentStepValid = currentStep
    ? isStepAnswerComplete(currentStep, currentAnswer || undefined)
    : false;
  // Anzahl der Punkte mit Nacharbeit - nur Hinweis, das Ticket entsteht serverseitig (one360-api).
  const reworkCount = steps.filter(
    (step) => answers[step.id]?.planned === false && answers[step.id]?.reworkRequired === true
  ).length;
  // Auch Alt-Entwürfe (vor #1459 gespeichert) können in der Zusammenfassung landen: Jeder
  // "Nein"-Punkt braucht daher beim Absenden eine Nacharbeit-Angabe, sonst entsteht still kein Ticket.
  const hasOpenReworkDecision = steps.some(
    (step) => answers[step.id]?.planned === false &&
      typeof answers[step.id]?.reworkRequired !== "boolean"
  );
  const canSubmit =
    !!template &&
    steps.length > 0 &&
    hasValidSummaryQuantities &&
    !hasOpenReworkDecision &&
    confirmationAccepted;

  const updateAnswer = (stepId: string, partial: Partial<AbschlussberichtAnswer>) => {
    const step = steps.find((entry) => entry.id === stepId);
    if (!step) {
      return;
    }

    setAnswers((previous) => {
      const existing = previous[stepId] || {
        stepId,
        itemNumber: step.itemNumber,
        displayTitle: step.displayTitle,
        planned: partial.planned ?? false,
      };

      return {
        ...previous,
        [stepId]: {
          ...existing,
          ...partial,
        },
      };
    });
  };

  const updateChecklistItem = (stepId: string, index: number, checked: boolean) => {
    setChecklistStates((previous) => {
      const nextStepState = [...(previous[stepId] || [])];
      for (let cursor = 0; cursor < index; cursor += 1) {
        if (typeof nextStepState[cursor] !== "boolean") {
          nextStepState[cursor] = false;
        }
      }
      nextStepState[index] = checked;

      return {
        ...previous,
        [stepId]: nextStepState,
      };
    });
  };

  const toggleDescriptionVisibility = (stepId: string) => {
    setDescriptionVisibility((previous) => ({
      ...previous,
      [stepId]: !previous[stepId],
    }));
  };

  const updateQuantityInput = (field: QuantityFieldKey, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) {
      return;
    }

    setQuantityInputs((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const incrementQuantity = (field: QuantityFieldKey, delta: number) => {
    setQuantityInputs((previous) => {
      const currentValue = getQuantityValue(previous[field]);
      const nextValue = Math.max(0, currentValue + delta);
      return {
        ...previous,
        [field]: String(nextValue),
      };
    });
  };

  const handleSubmit = async () => {
    if (!template || !canSubmit || submitting) {
      return;
    }

    const submittedByEmail = user?.email?.trim();
    if (!submittedByEmail) {
      showToast("error", "Bitte erneut anmelden, um den Abschlussbericht abzusenden");
      return;
    }

    if (
      summaryQuantities.ventileAnzahl === 0 &&
      !window.confirm("Achtung, prüfe ob du wirklich 0 Ventile verbaut hast.")
    ) {
      return;
    }

    if (isQueueItemInFlight(submitQueueKey)) {
      // Der vorherige Submit für dieses Projekt/Phase läuft noch (Request raus, Antwort
      // steht aus). addToQueue würde den noch nicht gesendeten, neueren (korrigierten)
      // Payload überschreiben — läuft der alte Request danach erfolgreich durch, löscht
      // processQueue das Item anhand der gemeinsamen queueKey-ID und die Korrektur geht
      // spurlos verloren, obwohl sie nie übertragen wurde (#976: "Positionen springen nach
      // erneuter Bearbeitung und Senden immer wieder auf Nein zurück").
      showToast(
        "info",
        "Abschlussbericht wird gerade synchronisiert — bitte kurz warten und erneut senden",
        5000
      );
      return;
    }

    const submission: AbschlussberichtSubmission = {
      projectId,
      sourceDocumentId: template.sourceDocumentId,
      sourceDocumentNumber: template.sourceDocumentNumber,
      templateKey: template.templateKey,
      templateVersion: template.templateVersion,
      submittedAt: new Date().toISOString(),
      submittedByEmail,
      answers: steps.map((step) => {
        const isWarrantyStep = step.id === warrantyPaymentStepId;
        return {
          stepId: step.id,
          itemNumber: step.itemNumber,
          displayTitle: step.displayTitle,
          planned: !!answers[step.id]?.planned,
          comment: answers[step.id]?.comment?.trim() || undefined,
          reworkRequired: answers[step.id]?.planned
            ? undefined
            : answers[step.id]?.reworkRequired,
          warrantyPaymentChoice: isWarrantyStep
            ? answers[step.id]?.warrantyPaymentChoice
            : undefined,
        };
      }),
      summaryQuantities,
    };

    setSubmitting(true);
    if (draftStorageKey) {
      window.localStorage.removeItem(draftStorageKey);
    }

    setLocalSubmission(submission);
    updatePhaseStatus(phaseId, "completed", submission.submittedAt, submission.submittedByEmail);
    onCompleted?.(submission.submittedAt);

    if (navigator.onLine) {
      showToast("success", "Abschlussbericht gespeichert", 3000);
    } else {
      showToast("info", "Abschlussbericht wurde offline in die Queue gelegt", 4000);
    }

    // Rollback nur bei einem WIRKLICH endgültigen Fehlschlag auslösen:
    // - addToQueue rejected: das Item konnte nicht mal lokal persistiert werden (z.B.
    //   Speicherplatz voll, siehe #1199) - eindeutig verloren, sofort zurückrollen.
    // - addToQueue resolved mit `permanentlyFailed`: das Item wurde lokal gespeichert,
    //   aber der Sync-Versuch scheiterte endgültig (z.B. 4xx vom Server) und wurde von
    //   processQueue bereits aus der Queue entfernt - ebenfalls endgültig verloren.
    // - `undefined`/`failed`-ohne-`permanentlyFailed` (offline, busy, transienter Fehler):
    //   das Item bleibt in der durablen Queue liegen und wird später erneut versucht -
    //   `queueSubmission` oben zeigt es währenddessen weiter als "vorhanden" an, kein
    //   Rollback nötig. Exakt das Muster, das für ReworkCommentThread (#1090) bereits
    //   gegen denselben Bug (totes Fehler-Netz, da addToQueue vorher nie rejected hat)
    //   eingeführt wurde.
    const rollback = (message: string) => {
      setLocalSubmission(null);
      updatePhaseStatus(phaseId, "pending");
      showToast("error", message, 4000);
    };

    addToQueue("abschlussbericht_submit", {
      queueKey: submitQueueKey,
      projectId,
      phaseId,
      submission,
    })
      .then((result) => {
        if (result && result.permanentlyFailed.length > 0) {
          rollback("Abschlussbericht konnte nicht gespeichert werden");
        }
      })
      .catch((submitError: unknown) => {
        if (submitError instanceof QueueItemLockTimeoutError) {
          // Ein Lock-Timeout ist ein UNBESTIMMTER Ausgang, kein definitiver Fehlschlag (siehe
          // QueueItemLockTimeoutError-Doku in queueService.ts) - addQueueItem läuft im
          // Hintergrund weiter und kann den Abschlussbericht noch erfolgreich vormerken. Ein
          // Rollback hier wäre die exakte destruktive Kompensation, vor der die
          // QueueItemLockTimeoutError-Doku warnt: der bereits optimistisch gesetzte lokale
          // Abschluss würde zurückgenommen, obwohl der Eintrag noch erfolgreich durchlaufen
          // könnte (PR-Review-Finding #976/#422).
          console.warn('[Abschlussbericht] Submit-Vormerkung hängt noch (Lock-Timeout):', submitError);
          return;
        }
        const message =
          submitError instanceof Error
            ? submitError.message
            : "Abschlussbericht konnte nicht gespeichert werden";
        rollback(message);
      })
      .finally(() => {
        // Erst hier zurücksetzen (statt synchron direkt nach setLocalSubmission): solange der
        // Request noch läuft, bleibt `submitting` true, damit isQueueItemInFlight oben einen
        // zwischenzeitlichen zweiten Submit-Versuch (z.B. nach "Zur Bearbeitung zurück")
        // zuverlässig abfangen kann, bevor der noch laufende alte Request beantwortet ist.
        setSubmitting(false);
      });
  };

  const handleRestartForEditing = () => {
    if (!readOnlySubmission) {
      return;
    }

    const confirmed = window.confirm(
      "Achtung: Der Abschlussbericht sollte nur in Ausnahmefällen neu gestartet werden. Möchtest du wirklich fortfahren?"
    );
    if (!confirmed) {
      return;
    }

    setAnswers(toAnswerMap(readOnlySubmission.answers));
    setQuantityInputs(normalizeQuantityInputs(readOnlySubmission.summaryQuantities));
    setChecklistStates({});
    setDescriptionVisibility({});
    setCurrentStepIndex(summaryStepIndex);
    setConfirmationAccepted(true);
    setLocalSubmission(null);
    setIsRestartedForEditing(true);
    updatePhaseStatus(phaseId, "pending");
    showToast("info", "Abschlussbericht kann wieder bearbeitet werden", 3000);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
          <p className="mt-5 text-base font-semibold text-gray-900 dark:text-white">
            Auftragsbestätigung wird analysiert
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Die KI strukturiert den Abschlussbericht. Das dauert in der Regel etwa 10 Sekunden.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Bitte kurz warten.</p>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error || "Abschlussbericht konnte nicht geladen werden"}
      </div>
    );
  }

  if (readOnlySubmission) {
    const submittedByEmail = readOnlySubmission.submittedByEmail?.trim();
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Baustellen-Abschlussbericht
            </h2>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isCompleted
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
              }`}
            >
              {isCompleted ? "Abgeschickt" : "In Queue"}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Quelle: {template.sourceDocumentNumber}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Übermittelt am {new Date(readOnlySubmission.submittedAt).toLocaleString("de-DE")}
          </p>
          {submittedByEmail && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Abgeschlossen von {submittedByEmail}
            </p>
          )}
          {"reworkTicketId" in readOnlySubmission && readOnlySubmission.reworkTicketId && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Nacharbeiten-Ticket: {readOnlySubmission.reworkTicketId}
            </p>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={handleRestartForEditing}
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
            >
              Zur Bearbeitung zurück
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {readOnlySubmission.answers.map((answer) => {
            const submittedStep = steps.find((step) => step.id === answer.stepId);
            const warrantyPaymentChoiceLabel = getWarrantyPaymentChoiceLabel(
              answer.warrantyPaymentChoice
            );
            const isDescriptionExpanded = !!descriptionVisibility[answer.stepId];

            return (
              <div
                key={answer.stepId}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
              >
                <div className="flex justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {answer.itemNumber}
                    </p>
                    {submittedStep?.quantityLabel && (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {submittedStep.quantityLabel}
                      </p>
                    )}
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {answer.displayTitle}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex h-8 min-w-[3.5rem] items-center justify-center self-start rounded-full px-3 text-sm font-semibold ${
                      answer.planned
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    }`}
                  >
                    {answer.planned ? "Ja" : "Nein"}
                  </span>
                </div>
                {warrantyPaymentChoiceLabel && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    Zahlweise: {warrantyPaymentChoiceLabel}
                  </p>
                )}
                {!answer.planned && answer.comment && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {answer.comment}
                  </p>
                )}
                {!answer.planned && typeof answer.reworkRequired === "boolean" && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    Nacharbeit erforderlich: {answer.reworkRequired ? "Ja" : "Nein"}
                  </p>
                )}
                {submittedStep?.descriptionHtml && (
                  <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleDescriptionVisibility(answer.stepId)}
                      className="flex w-full items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-left"
                    >
                      <span>
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                          Beschreibung
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {isDescriptionExpanded ? "Ausblenden" : "Bei Bedarf aufklappen"}
                        </span>
                      </span>
                      <span
                        className={`text-gray-500 dark:text-gray-400 transition-transform ${
                          isDescriptionExpanded ? "rotate-180" : ""
                        }`}
                        aria-hidden="true"
                      >
                        ▼
                      </span>
                    </button>

                    {isDescriptionExpanded && (
                      <div
                        className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 text-sm text-gray-700 dark:text-gray-300 [&_p]:mb-3 last:[&_p]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:ml-4 [&_ul]:mb-4 [&_ul]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:ml-4 [&_ol]:mb-4 [&_ol]:mt-2 [&_li]:mb-1 [&_ul+p]:mt-4 [&_ol+p]:mt-4"
                        dangerouslySetInnerHTML={{ __html: submittedStep.descriptionHtml }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Mengenangaben</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Anzahl Ventile
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {readOnlySubmission.summaryQuantities.ventileAnzahl}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Rohre innen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {readOnlySubmission.summaryQuantities.rohreInnen}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Unterirdisch verlegte Rohre außen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {readOnlySubmission.summaryQuantities.rohreAussen}
                </p>
              </div>
            </div>
          </div>
        </div>

        {isQueued && (
          <div className="px-4 pb-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 p-3 text-sm">
              Der Abschlussbericht wartet auf den nächsten erfolgreichen Sync.
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b dark:border-gray-700">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Baustellen-Abschlussbericht
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Quelle: {template.sourceDocumentNumber}
            </p>
          </div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {isSummaryStep
              ? "Zusammenfassung"
              : isQuantitiesStep
                ? `Schritt ${quantitiesStepIndex + 1}/${summaryStepIndex + 1}`
                : `Schritt ${currentStepIndex + 1}/${summaryStepIndex + 1}`}
          </span>
        </div>
        {isQuantitiesStep && isFoerderhoeheHinweisEnabled() && (
          <p
            data-testid="abschlussbericht-foerderhoehe-hinweis"
            className="mt-3 text-sm text-gray-600 dark:text-gray-300"
          >
            Die Förderhöhe der Wasserpumpe wird aus dem VdZ in den
            Abschlussbericht übernommen, sofern sie dort erfasst wurde.
          </p>
        )}
      </div>

      {!isQuantitiesStep && !isSummaryStep && currentStep ? (
        <div className="p-4">
          <div className="mb-4 min-h-[8.25rem]">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              {currentStep.itemNumber}
            </p>
            {currentStep.quantityLabel && (
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {currentStep.quantityLabel}
              </p>
            )}
            <h3 className="min-h-[5.25rem] text-xl font-semibold leading-7 text-gray-900 dark:text-white">
              {currentStep.displayTitle}
            </h3>
            {currentStep.groupTitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {currentStep.groupTitle}
              </p>
            )}
          </div>

          {currentStep.descriptionHtml && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {!isProjectCompletionStep(currentStep.id) && (
                <button
                  type="button"
                  onClick={() => toggleDescriptionVisibility(currentStep.id)}
                  className="flex w-full items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-left"
                >
                  <span>
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      Beschreibung
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {isCurrentDescriptionExpanded ? "Ausblenden" : "Bei Bedarf aufklappen"}
                    </span>
                  </span>
                  <span
                    className={`text-gray-500 dark:text-gray-400 transition-transform ${
                      isCurrentDescriptionExpanded ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  >
                    ▼
                  </span>
                </button>
              )}

              {isCurrentDescriptionExpanded && (
                <div
                  className={`${isProjectCompletionStep(currentStep.id) ? "" : "border-t border-gray-200 dark:border-gray-700 "}bg-gray-50 dark:bg-gray-900/40 p-4 text-sm text-gray-700 dark:text-gray-300 [&_p]:mb-3 last:[&_p]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:ml-4 [&_ul]:mb-4 [&_ul]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:ml-4 [&_ol]:mb-4 [&_ol]:mt-2 [&_li]:mb-1 [&_ul+p]:mt-4 [&_ol+p]:mt-4`}
                  dangerouslySetInnerHTML={{ __html: currentStep.descriptionHtml }}
                />
              )}
            </div>
          )}

          {SHOW_POSITION_CHECKLIST && currentChecklistItems.length > 0 && (
            <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Positionen abhaken
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Wenn alle Positionen erledigt sind, wird &quot;Ja&quot; automatisch gesetzt.
                </p>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {currentChecklistItems.map((item, index) => (
                  <label
                    key={`${currentStep.id}-${index}`}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                  >
                    <span className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                    <input
                      type="checkbox"
                      checked={!!currentChecklistState[index]}
                      onChange={(event) =>
                        updateChecklistItem(currentStep.id, index, event.target.checked)
                      }
                      disabled={disabled}
                      className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              {isProjectCompletionStep(currentStep.id)
                ? "Ist das Projekt vollständig abgeschlossen?"
                : "Lief wie geplant?"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => updateAnswer(currentStep.id, {
                  planned: true,
                  comment: undefined,
                  reworkRequired: undefined,
                })}
                disabled={disabled}
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  currentAnswer?.planned
                    ? "border-green-500 bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
              >Ja</button>
              <button
                type="button"
                onClick={() => updateAnswer(currentStep.id, { planned: false })}
                disabled={disabled}
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  currentAnswer?.planned === false
                    ? "border-red-500 bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
              >Nein</button>
            </div>
          </div>

          {currentStepRequiresWarrantyPaymentChoice && (
            <fieldset className="mt-5 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <legend className="px-1 text-sm font-medium text-gray-900 dark:text-white">
                Zahlweise der Garantie
              </legend>
              <div className="mt-3 space-y-3">
                {WARRANTY_PAYMENT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
                  >
                    <input
                      type="radio"
                      name={`abschlussbericht-warranty-payment-${currentStep.id}`}
                      value={option.value}
                      checked={currentAnswer?.warrantyPaymentChoice === option.value}
                      onChange={() =>
                        updateAnswer(currentStep.id, {
                          warrantyPaymentChoice: option.value,
                        })
                      }
                      disabled={disabled}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {currentAnswer?.planned === false && (
            <div className="mt-4">
              <label
                htmlFor={`abschlussbericht-comment-${currentStep.id}`}
                className="block text-sm font-medium text-gray-900 dark:text-white mb-2"
              >
                Kommentar
              </label>
              <textarea
                id={`abschlussbericht-comment-${currentStep.id}`}
                value={currentAnswer.comment || ""}
                onChange={(event) => updateAnswer(currentStep.id, { comment: event.target.value })}
                disabled={disabled}
                rows={4}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                placeholder="Bitte die Abweichung kurz beschreiben"
              />
            </div>
          )}

          {currentAnswer?.planned === false && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Nacharbeit erforderlich?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => updateAnswer(currentStep.id, { reworkRequired: true })}
                  disabled={disabled}
                  className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                    currentAnswer.reworkRequired === true
                      ? "border-green-500 bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                      : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                  }`}
                >Ja</button>
                <button
                  type="button"
                  onClick={() => updateAnswer(currentStep.id, { reworkRequired: false })}
                  disabled={disabled}
                  className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                    currentAnswer.reworkRequired === false
                      ? "border-red-500 bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200"
                      : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                  }`}
                >Nein</button>
              </div>
            </div>
          )}
        </div>
      ) : isQuantitiesStep ? (
        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Mengenangaben</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Bitte die tatsächlich verbauten Mengen eintragen. Alle drei Felder sind Pflicht.
            </p>
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
              ⚠️ Bei den Rohrlängen innen und außen gilt: Es ist die gesamte verbaute
              Rohrleitungsmenge zu erfassen. Wenn der Abstand zwischen Innengerät und Aussengerät 10m beträgt, ist hier 20m einzutragen.
            </div>
          </div>

          {[
            { field: "ventileAnzahl" as const, label: "Anzahl Ventile" },
            {
              field: "rohreInnen" as const,
              label: "Rohre innen",
            },
            {
              field: "rohreAussen" as const,
              label: "Unterirdisch verlegte Rohre außen",
            },
          ].map(({ field, label }) => (
            <div
              key={field}
              className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
            >
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                {label}
              </label>
              <div className="flex items-stretch overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700 max-w-sm">
                <button
                  type="button"
                  onClick={() => incrementQuantity(field, -1)}
                  disabled={disabled}
                  className="w-14 bg-gray-100 dark:bg-gray-900 text-2xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  -
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  value={quantityInputs[field]}
                  onChange={(event) => updateQuantityInput(field, event.target.value)}
                  disabled={disabled}
                  className="w-full min-w-0 border-x border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 text-center text-lg font-semibold text-gray-900 dark:text-white"
                  placeholder="0 erlaubt"
                />
                <button
                  type="button"
                  onClick={() => incrementQuantity(field, 1)}
                  disabled={disabled}
                  className="w-14 bg-green-500 text-2xl font-semibold text-white"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {steps.map((step) => {
            const answer = answers[step.id];
            const warrantyPaymentChoiceLabel = getWarrantyPaymentChoiceLabel(
              answer?.warrantyPaymentChoice
            );
            return (
              <div
                key={step.id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
              >
                <div className="flex justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {step.itemNumber}
                    </p>
                    {step.quantityLabel && (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {step.quantityLabel}
                      </p>
                    )}
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {step.displayTitle}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex h-8 min-w-[3.5rem] items-center justify-center self-start rounded-full px-3 text-sm font-semibold ${
                      answer?.planned
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    }`}
                  >
                    {answer?.planned ? "Ja" : "Nein"}
                  </span>
                </div>
                {answer?.planned === false && answer.comment && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {answer.comment}
                  </p>
                )}
                {answer?.planned === false && typeof answer.reworkRequired === "boolean" && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    Nacharbeit erforderlich: {answer.reworkRequired ? "Ja" : "Nein"}
                  </p>
                )}
                {warrantyPaymentChoiceLabel && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    Zahlweise: {warrantyPaymentChoiceLabel}
                  </p>
                )}
              </div>
            );
          })}

          {reworkCount > 0 && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Für {reworkCount} {reworkCount === 1 ? "Punkt" : "Punkte"} wird Nacharbeit angelegt (ein Ticket pro Bericht).
            </p>
          )}

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Mengenangaben</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Anzahl Ventile
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryQuantities.ventileAnzahl}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Rohre innen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryQuantities.rohreInnen}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Unterirdisch verlegte Rohre außen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryQuantities.rohreAussen}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-medium mb-2">Finale Bestätigung</p>
            <p>
              Ich verstehe, wie wichtig dieser Bericht ist. Änderungen am Projekt im Vergleich zum
              Angebot sind in Ordnung, aber wir müssen darüber informiert werden. Wenn sich im
              Nachhinein herausstellt, dass wesentliche Abweichungen nicht angegeben wurden, kann
              sich das negativ auf meinen Baustellenbonus auswirken.
            </p>

            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmationAccepted}
                onChange={(event) => setConfirmationAccepted(event.target.checked)}
                disabled={disabled || submitting}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-amber-950">
                Ich bestätige, zur Kenntnis genommen.
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="p-4 border-t dark:border-gray-700 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCurrentStepIndex((previous) => Math.max(0, previous - 1))}
          disabled={currentStepIndex === 0 || disabled || submitting}
          className="min-h-[48px] rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
        >
          Zurück
        </button>

        {!isSummaryStep ? (
          <button
            type="button"
            onClick={() =>
              setCurrentStepIndex((previous) => Math.min(summaryStepIndex, previous + 1))
            }
            disabled={
              disabled ||
              (!isQuantitiesStep && !isCurrentStepValid) ||
              (isQuantitiesStep && !hasValidSummaryQuantities)
            }
            className="min-h-[48px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {currentStepIndex === steps.length - 1
              ? "Zu den Mengen"
              : isQuantitiesStep
                ? "Zur Zusammenfassung"
                : "Weiter"}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || disabled || submitting}
            className="min-h-[48px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Wird gesendet..." : "Absenden"}
          </button>
        )}
      </div>
    </div>
  );
};

export default AbschlussberichtWizard;
